/**
 * PushNotificationsCard (OCP-331) — the operator enables Web Push on the
 * current device from the dashboard. Surfaces the right state: support,
 * server config (VAPID), permission, subscription; offers enable /
 * disable / send-test.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import {
  disablePush,
  enablePush,
  getPushStatus,
  pushSupported,
  sendTestPush,
  type PushStatus,
} from '../lib/push';

export function PushNotificationsCard() {
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setStatus(await getPushStatus());
    } catch {
      setStatus({ supported: pushSupported(), permission: 'default', subscribed: false, configured: false });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const run = useCallback(
    async (fn: () => Promise<void>, ok: string) => {
      setBusy(true);
      setError(null);
      setNote(null);
      try {
        await fn();
        setNote(ok);
        await refresh();
      } catch (err) {
        const code = err instanceof Error ? err.message : 'failed';
        setError(
          code === 'permission_denied'
            ? 'Notifications were blocked. Enable them in your browser/OS settings, then retry.'
            : code === 'push_not_configured'
              ? 'Push is not configured on the server yet (VAPID keys missing).'
              : code === 'push_unsupported'
                ? 'This browser does not support push notifications.'
                : 'Something went wrong. Please try again.',
        );
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  const card = (children: ReactNode) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Push notifications</div>
          <div className="mt-0.5 text-xs text-gray-500">
            Get a push on new waitlist / self-host / customer signups + tenants going live.
          </div>
        </div>
        {children}
      </div>
      {note && <div className="mt-3 text-xs text-emerald-600 dark:text-emerald-400">{note}</div>}
      {error && <div className="mt-3 text-xs text-red-600 dark:text-red-400">{error}</div>}
    </div>
  );

  if (!status) return card(<span className="text-xs text-gray-400">…</span>);

  if (!status.supported) {
    return card(
      <span className="text-xs text-gray-500">
        Not supported here. On iPhone: Share → Add to Home Screen, then open the installed app.
      </span>,
    );
  }

  if (!status.configured) {
    return card(<span className="text-xs text-gray-500">Server not configured (VAPID keys missing).</span>);
  }

  const btn =
    'rounded px-3 py-1.5 text-xs font-medium disabled:opacity-50';

  if (status.subscribed) {
    return card(
      <div className="flex shrink-0 items-center gap-2">
        <span className="inline-flex items-center rounded bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 ring-1 ring-inset ring-emerald-200">
          On for this device
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(sendTestPush, 'Test notification sent.')}
          className={`${btn} border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800`}
        >
          Send test
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(disablePush, 'Notifications turned off on this device.')}
          className={`${btn} border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800`}
        >
          Disable
        </button>
      </div>,
    );
  }

  return card(
    <button
      type="button"
      disabled={busy}
      onClick={() => void run(enablePush, 'Notifications enabled on this device.')}
      className={`${btn} shrink-0 bg-brand-500 text-white hover:bg-brand-600`}
    >
      {busy ? 'Enabling…' : 'Enable on this device'}
    </button>,
  );
}
